import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { setCustomerByName } from "src/slices/customers";
import {
  getArkaduSessionFromToken,
  loadUserFromStorage,
} from "src/slices/users";
import { useDispatch, useSelector } from "src/store";
import LoadingScreen from "./LoadingScreen";

const GuestGuard = ({ children }) => {
  const { isAuthenticated, loaded, notifyMessage } = useSelector(
    (state) => state.users,
  );
  const { customers, currentCustomer, customersLoaded } = useSelector(
    (state) => state.customers,
  );

  const { customerName } = useParams();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [redirectPath, setRedirectPath] = useState(null);
  const defaultRedirectPath = customerName ? `/${customerName}/` : "/app/";

  const resolveNextPath = () => {
    const searchParams = new URLSearchParams(location.search);
    const queryNext = searchParams.get("next");
    const storedNext = localStorage.getItem("next");
    const candidate = queryNext || storedNext;

    if (
      typeof candidate === "string" &&
      candidate.startsWith("/") &&
      !candidate.startsWith("//")
    ) {
      return candidate;
    }

    return null;
  };

  useEffect(() => {
    if (!loaded) {
      dispatch(loadUserFromStorage());
    }
  }, [loaded, dispatch]);

  useEffect(() => {
    if (!customersLoaded && (!customers || customers.length === 0)) {
      return;
    }

    if (
      customerName &&
      (!currentCustomer || currentCustomer.shortname !== customerName) &&
      customers
    ) {
      dispatch(setCustomerByName({ shortname: customerName }));
    }
  }, [customerName, customers, currentCustomer, customersLoaded, dispatch]);

  useEffect(() => {
    if (notifyMessage) {
      enqueueSnackbar(notifyMessage.message, {
        variant: notifyMessage.variant,
        anchorOrigin: {
          horizontal: "center",
          vertical: "bottom",
        },
      });
    }
  }, [notifyMessage, enqueueSnackbar]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getArkaduSessionFromToken());
      const nextUrl = resolveNextPath();
      localStorage.removeItem("next");
      if (
        nextUrl &&
        (nextUrl.startsWith("/certificate/") || nextUrl.startsWith("/api/"))
      ) {
        window.location.replace(nextUrl);
        return;
      }

      setRedirectPath(nextUrl || defaultRedirectPath);
    }
  }, [isAuthenticated, dispatch, defaultRedirectPath, location.search]);

  if (!loaded) {
    return (
      <LoadingScreen message="loading" suppressWhenGlobalLoading={false} />
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isAuthenticated) {
    return <Navigate to={defaultRedirectPath} replace />;
  }

  return <>{children}</>;
};

GuestGuard.propTypes = {
  children: PropTypes.node,
};

export default GuestGuard;
